import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { useWebSocket } from '@/hooks/use-websocket'

type AnyRecord = Record<string, unknown>

function asRecord(value: unknown): AnyRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as AnyRecord)
    : null
}

/**
 * The event struct inside a frame. A deployed frame is the bare struct. The local-dev server wraps
 * it as `{ event, message: '<json string>' }`, and the client may nest it under `data`.
 */
function readPayload(message: unknown): AnyRecord | null {
  const frame = asRecord(message)

  if (!frame) return null

  if (typeof frame.message === 'string') {
    try {
      return asRecord(JSON.parse(frame.message))
    } catch {
      return null
    }
  }

  return asRecord(frame.data) ?? frame
}

/** True for a change to the book's custom words: a word added or removed, or the list cleared. */
function isCustomDictionaryChange(message: unknown): boolean {
  const payload = readPayload(message)

  if (!payload || typeof payload.DictionaryAction !== 'string') return false

  return payload.DictionaryLayer === undefined || payload.DictionaryLayer === 'custom'
}

/**
 * Keeps the dictionary in step with other editors of the book. The backend sends a
 * `DictionaryChangedEvent` to everyone else editing the book (never to the connection that made the
 * change), and the cached dictionary is marked stale so it is read again: at once while the Spell
 * Check panel is open, otherwise the next time it opens. Re-reading covers an add, a remove and a
 * clear alike and always shows what the server holds.
 *
 * The backend registers a connection for those events when it calls `GET /dictionary` (for 2
 * hours), so a connection made after a reconnect is not registered until the dictionary is read
 * again. A new connection id therefore marks the dictionary stale too.
 */
export function useDictionarySync(enabled = true) {
  const { subscribe, connectionId } = useWebSocket()
  const queryClient = useQueryClient()
  const lastConnectionIdRef = useRef<string | null>(null)

  // Reacts to an outside event, the socket coming back with a new id, so it cannot be derived. The
  // last id is kept across a drop (the id goes to null) so the id that follows still counts as new.
  useEffect(() => {
    if (!connectionId) return

    const previous = lastConnectionIdRef.current

    lastConnectionIdRef.current = connectionId

    if (enabled && previous && previous !== connectionId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dictionary.all })
    }
  }, [connectionId, enabled, queryClient])

  useEffect(() => {
    if (!enabled) return

    return subscribe('*', (message) => {
      if (!isCustomDictionaryChange(message)) return

      void queryClient.invalidateQueries({ queryKey: queryKeys.dictionary.all })
    })
  }, [enabled, subscribe, queryClient])
}
